import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { createStore } from './create-store';

/**
 * FRONT-542 — which finished background-chat runs the user has actually *read*.
 *
 * A chat's status light has three stages: blue (still working), green (the
 * answer is ready but you haven't opened it yet), and grey (you've seen it, or
 * there's nothing going on). The green→grey step needs a client-side memory of
 * "I've looked at this answer" — there's no server concept of read, and there
 * shouldn't be (it's a per-device reading state). The chat modal records a
 * run's id here the moment you open it on a finished answer; the pill dots and
 * the inbox row dot then read this set and drop to grey.
 *
 * Keyed by `runId` (the stable per-run id) so a brand-new turn on the same chat
 * starts unread again — re-arming the light when EDDY has something new to show.
 *
 * Persisted to localStorage (and cross-tab synced) so the grey state survives a
 * refresh — mirrors the detached/dismissed stores.
 */

const STORAGE_KEY = 'eddy.read-bg-chats';
const MAX_IDS = 300;

function loadIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function persistIds(ids: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* quota or storage disabled — read state becomes session-only */
  }
}

export interface ReadBgChatsStore {
  ids: Set<string>;
  /** Mark a finished run as read so its light drops to grey. Idempotent. */
  markRead: (runId: string) => void;
  clear: () => void;
}

export function createReadBgChatsStore(): StoreApi<ReadBgChatsStore> {
  return createStore<ReadBgChatsStore>('read-bg-chats-store', (set, get) => ({
    ids: new Set(loadIds()),
    markRead: (runId) => {
      if (!runId || get().ids.has(runId)) return;
      const arr = [...get().ids, runId];
      // FIFO trim so the entry can't grow without bound after months of use —
      // old run ids age out of the server's recent list anyway.
      const trimmed = arr.length > MAX_IDS ? arr.slice(arr.length - MAX_IDS) : arr;
      persistIds(trimmed);
      set({ ids: new Set(trimmed) });
    },
    clear: () => {
      persistIds([]);
      set({ ids: new Set() });
    },
  }));
}

let store: StoreApi<ReadBgChatsStore> | undefined;

function getStore(): StoreApi<ReadBgChatsStore> {
  if (!store) {
    store = createReadBgChatsStore();
    // Cross-tab sync: reading an answer in one tab greys the light in the other.
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key !== STORAGE_KEY || !store) return;
        try {
          const parsed = e.newValue ? (JSON.parse(e.newValue) as string[]) : [];
          store.setState({ ids: new Set(parsed) });
        } catch {
          /* malformed value — ignore */
        }
      });
    }
  }
  return store;
}

export function useReadBgChats<T>(selector: (s: ReadBgChatsStore) => T): T {
  return useStore(getStore(), selector);
}
